import React, { useState } from 'react';
import { hasPermission } from '../utils/permissions';
import { PERMISSIONS } from '../constants/permissions';
import { Tabs, Tab, Box } from '@mui/material';
import PlatformGeneralSettings from './PlatformGeneralSettings.jsx';
import GlobalIntegrationsSettings from './GlobalIntegrationsSettings.jsx';
import IntegrationsSettings from './IntegrationsSettings.jsx';
import AppearanceSettings from './AppearanceSettings.jsx';
import RelayAgentsSettings from './RelayAgentsSettings.jsx';
import SecuritySettings from './SecuritySettings.jsx';
import DiagnosticsSettings from './DiagnosticsSettings.jsx';
import PlatformVariablesTab from './PlatformVariablesTab.jsx';
import KeyRotationPage from './KeyRotationPage.jsx';
import AboutSettings from './AboutSettings.jsx';
import { DynamicIcon } from '../components/icons.jsx';

const PlatformSettings = ({ token, currentUser }) => {
    const [activeTab, setActiveTab] = useState(0);

    if (!hasPermission(currentUser, 'page:settings:view'))
        return <div className="p-8 text-gray-500">Access Denied</div>;

    return (
        <div className="flex flex-col h-[calc(100vh-64px)] overflow-hidden bg-slate-50 dark:bg-slate-900">
            {/* Tab bar */}
            <div className="flex-shrink-0 px-6 pt-2 pb-0 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800">
                <Box sx={{ width: '100%' }}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => setActiveTab(v)}
                        aria-label="platform settings tabs"
                        sx={{
                            minHeight: '48px',
                            '& .MuiTab-root': {
                                minHeight: '48px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                color: 'text.secondary',
                                '&.Mui-selected': { color: 'primary.main' },
                            },
                        }}
                    >
                        <Tab value={0} label="General"      id="tab-0" icon={<DynamicIcon name="Settings"    className="w-4 h-4 mb-0 mr-2" />} iconPosition="start" />
                        <Tab value={1} label="Integrations" id="tab-1" icon={<DynamicIcon name="Link"        className="w-4 h-4 mb-0 mr-2" />} iconPosition="start" />
                        <Tab value={2} label="Hybrid Agents" id="tab-2" icon={<DynamicIcon name="Server"      className="w-4 h-4 mb-0 mr-2" />} iconPosition="start" />
                        <Tab value={3} label="Appearance"   id="tab-3" icon={<DynamicIcon name="Palette"     className="w-4 h-4 mb-0 mr-2" />} iconPosition="start" />
                        <Tab value={4} label="Security"     id="tab-4" icon={<DynamicIcon name="ShieldCheck" className="w-4 h-4 mb-0 mr-2" />} iconPosition="start" />
                        <Tab value={5} label="Diagnostics"  id="tab-5" icon={<DynamicIcon name="Activity"    className="w-4 h-4 mb-0 mr-2" />} iconPosition="start" />
                        {hasPermission(currentUser, PERMISSIONS.SETTINGS_PLATFORM_EDIT) && (
                            <Tab value={6} label="System Variables" id="tab-6" icon={<DynamicIcon name="Database" className="w-4 h-4 mb-0 mr-2" />} iconPosition="start" />
                        )}
                        {currentUser?.roleId === 'r1' && (
                            <Tab value={7} label="Key Rotation" id="tab-7" icon={<DynamicIcon name="KeyRound" className="w-4 h-4 mb-0 mr-2" />} iconPosition="start" />
                        )}
                        <Tab value={8} label="About" id="tab-8" icon={<DynamicIcon name="Info" className="w-4 h-4 mb-0 mr-2" />} iconPosition="start" />
                    </Tabs>
                </Box>
            </div>

            {/* Scrollable content */}
            <div className="flex-grow overflow-y-auto p-6">
                <Box role="tabpanel" hidden={activeTab !== 0}>
                    {activeTab === 0 && <PlatformGeneralSettings currentUser={currentUser} token={token} />}
                </Box>
                <Box role="tabpanel" hidden={activeTab !== 1}>
                    {activeTab === 1 && (
                        hasPermission(currentUser, PERMISSIONS.SETTINGS_INTEGRATIONS_GLOBAL)
                        ? <GlobalIntegrationsSettings token={token} />
                        : <IntegrationsSettings token={token} tenantId={currentUser.tenantId} />
                    )}
                </Box>
                <Box role="tabpanel" hidden={activeTab !== 2}>
                    {activeTab === 2 && <RelayAgentsSettings token={token} />}
                </Box>
                <Box role="tabpanel" hidden={activeTab !== 3}>
                    {activeTab === 3 && <AppearanceSettings token={token} />}
                </Box>
                <Box role="tabpanel" hidden={activeTab !== 4}>
                    {activeTab === 4 && <SecuritySettings token={token} />}
                </Box>
                <Box role="tabpanel" hidden={activeTab !== 5}>
                    {activeTab === 5 && <DiagnosticsSettings />}
                </Box>
                <Box role="tabpanel" hidden={activeTab !== 6}>
                    {activeTab === 6 && <PlatformVariablesTab token={token} currentUser={currentUser} />}
                </Box>
                <Box role="tabpanel" hidden={activeTab !== 7}>
                    {activeTab === 7 && <KeyRotationPage token={token} />}
                </Box>
                <Box role="tabpanel" hidden={activeTab !== 8}>
                    {activeTab === 8 && <AboutSettings currentUser={currentUser} token={token} />}
                </Box>
            </div>
        </div>
    );
};

export default PlatformSettings;
